<template>
  <div class="group-question-selector">
    <TableList
      ref="tableList"
      row-key="_id"
      no-operation
      :show-create-btn="false"
      :default-expand-all="false"
      :list-data="searchResult"
      :table-columns="tableColumns"
      :tree-props="{children: 'searchedChildren', hasChildren: 'hasChildren'}"
    >
      <template #filter-group>
        <el-select
          v-model="search.groupId"
          filterable
          clearable
          class="group-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.groupFilter`)"
        >
          <el-option
            v-for="(item, index) in groupList"
            :key="index"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-if="questionTypeList.length > 1"
          v-model="search.questionType"
          filterable
          clearable
          class="question-type-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.questionTypeFilter`)"
        >
          <el-option
            v-for="(item, index) in questionTypeList"
            :key="index"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-input
          v-model="search.keywords"
          clearable
          class="keywords-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.groupQuestionKeywordsFilter`)"
        />
      </template>
      <template #headerCheckbox>
        <el-checkbox
          :value="isCheckedRow(null, null, true)"
          :indeterminate="isIndeterminateRow(null, null, true)"
          @change="handleRowCheckedChange(null, null, true)"
        />
      </template>
      <template #bodyCheckbox="{ row, data }">
        <el-checkbox
          :value="isCheckedRow(row, data)"
          :indeterminate="isIndeterminateRow(row, data)"
          @change="handleRowCheckedChange(row, data)"
        />
      </template>
      <template #questionTitle="{row, formattedData}">
        <template v-if="formattedData && HAS_OPTIONS_QUESTION_TYPES.includes(row.type)">
          <div class="question-title-wrapper">
            <span class="title" :title="formattedData">{{ formattedData }}</span>
            <el-button type="text" class="expand-btn" @click="handlerExpandQuestion($event, row)">{{ $t(`${i18nPrefix}.btn.questionOptions.${questionExpand[row.id] ? 'collapse' : 'expand'}`) }}</el-button>
          </div>
          <transition name="flip" @before-enter="handleTransitionBeforeEnter" @before-leave="handleTransitionBeforeLeave">
            <ul v-show="questionExpand[row.id]" class="question-options">
              <li
                v-for="(item, index) in row.options"
                class="option-item"
                :key="index"
                :class="item.is_knockout && 'knockout'"
              >{{ item.description }}</li>
            </ul>
          </transition>
        </template>
        <span v-else :title="formattedData">{{ formattedData }}</span>
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/common.vue'
import { DATA_TYPE, QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { i18nPrefix, parseGroup } from './common'

export default {
  components: {
    TableList
  },
  model: {
    prop: 'selected',
    event: 'change'
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    selected: {
      type: Array,
      required: true
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      i18nPrefix,
      QUESTION_TYPE,
      HAS_OPTIONS_QUESTION_TYPES,
      groupList: [],
      selectedQuestion: {},
      questionExpand: {},
      groupSelection: {},
      search: {
        groupId: null,
        questionType: null,
        keywords: null
      }
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    selectedWatcher () {
      return this.selected
    },
    searchResult () {
      const result = _.filter(this.groupList, group => {
        group.searchedChildren = _.filter(group.children, 'isShow')
        return group.isShow
      })
      return result
    },
    questionList () {
      return _.flatMap(this.groupList, 'children')
    },
    questionTypeList () {
      return _
        .chain(QUESTION_TYPE)
        .map((value, key) => {
          return { label: this.$t(`${this.i18nPrefix}.questionType.${key}`), value }
        })
        .filter(({ value }) => !this.disabledQuestionType.includes(value))
        .value()
    },
    tableRef () {
      return this.$refs.tableList.elTable
    },
    tableColumns () {
      const vm = this
      const columns = [
        {
          columnType: 'selection',
          width: 25,
          dataKey: '_id',
          slotName: 'bodyCheckbox',
          headerSlotName: 'headerCheckbox'
        },
        {
          width: 80,
          label: this.$t(`${this.i18nPrefix}.label.groupName`),
          cellClass: 'group-name',
          dataKey: 'name',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.GROUP) {
              return data
            }
          }
        },
        {
          width: 50,
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          dataKey: 'type',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              const type = _.findKey(QUESTION_TYPE, item => item === data)
              return vm.$t(`${vm.i18nPrefix}.questionType.${type}`)
            }
          }
        },
        {
          width: 150,
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          dataKey: 'title',
          cellClass: 'question-title',
          slotName: 'questionTitle',
          showOverflowTooltip: false,
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              return data
            }
          }
        }
      ]
      return columns
    }
  },
  created () {

  },
  mounted () {
    const tableRef = this.tableRef
    const selectionCol = _.find(tableRef.$children, { type: 'selection' })
    const colConfig = selectionCol.columnConfig
    const originRenderHeader = colConfig.renderHeader
    colConfig.renderHeader = (h, scope) => {
      const renderHeader = selectionCol.$scopedSlots.header
      return renderHeader ? renderHeader(scope) : originRenderHeader(h, scope)
    }
  },
  methods: {
    refreshGroupList () {
      const search = this.search
      const questionExpand = {}
      const groupList = this.data.map(parseGroup).filter(group => group.children?.length > 0)
      groupList.forEach(group => {
        Object.defineProperty(group, 'isShow', {
          configurable: true,
          enumerable: true,
          get () {
            let show = this.searchedChildren.length > 0
            if (show && search.groupId) {
              show = this.id === search.groupId
            }
            return show
          }
        })
        const children = []
        _.forEach(group.children, question => {
          const questionType = question.type
          if (this.disabledQuestionType.includes(questionType)) {
            return
          }
          questionExpand[question.id] = false
          Object.defineProperty(question, 'isShow', {
            configurable: true,
            enumerable: true,
            get () {
              let show = true
              if (search.questionType) {
                show = this.type === search.questionType
              }
              if (show && search.keywords) {
                show = this.title.includes(search.keywords)
              }
              return show
            }
          })
          children.push(question)
        })
        group.children = children
        group.searchedChildren = children
      })
      this.questionExpand = questionExpand
      this.groupList = groupList
    },
    refreshSelected () {
      const selectedQuestion = {}
      _.forEach(this.selected, question => {
        selectedQuestion[question.id] = question
      })
      _.forEach(this.questionList, question => {
        if (!selectedQuestion[question.id]) {
          selectedQuestion[question.id] = null
        }
      })
      this.selectedQuestion = selectedQuestion
    },
    isQuestionRow (row, data) {
      return Boolean(row._parent)
    },
    isCheckedRow (row, data, isHeader) {
      if (isHeader) {
        return this.selected.length > 0 && this.selected.length === this.questionList.length
      } else if (this.isQuestionRow(row, data)) {
        return Boolean(this.selectedQuestion[row.id])
      } else {
        const groupQuestionList = row.children
        if (groupQuestionList.length > 0) {
          const checked = _.filter(groupQuestionList, question => this.selectedQuestion[question.id])
          return checked.length === groupQuestionList.length
        } else {
          return this.isCheckedRow(null, null, true)
        }
      }
    },
    isIndeterminateRow (row, data, isHeader) {
      if (isHeader) {
        return this.selected.length > 0 && this.selected.length < this.questionList.length
      } else if (this.isQuestionRow(row, data)) {
        return false
      } else {
        const checked = _.filter(row.children, question => this.selectedQuestion[question.id])
        return checked.length > 0 && checked.length < row.children.length
      }
    },
    handleRowCheckedChange (row, data, isHeader) {
      const isChecked = this.isCheckedRow(row, data, isHeader)
      if (isHeader) {
        this.changeSelected(!isChecked)
      } else if (this.isQuestionRow(row, data)) {
        this.changeSelected(!isChecked, row)
      } else {
        this.changeSelected(!isChecked, null, row)
      }
    },
    changeSelected (isAdd, question, group) {
      let selected = null
      if (!question && !group) {
        if (isAdd) {
          selected = this.questionList
        } else {
          selected = []
        }
      } else {
        if (question) {
          this.selectedQuestion[question.id] = isAdd ? question : null
        } else {
          _.forEach(group.children, question => {
            this.selectedQuestion[question.id] = isAdd ? question : null
          })
        }
        selected = _.filter(this.questionList, question => this.selectedQuestion[question.id])
      }
      this.$emit('change', selected)
    },
    handlerExpandQuestion ($event, question) {
      const isExpand = this.questionExpand[question.id]
      this.questionExpand[question.id] = !isExpand
    },
    handleTransitionBeforeEnter (el) {
      el.style.display = null
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--height', `${rect.height}px`)
      el.style.display = 'none'
    },
    handleTransitionBeforeLeave (el) {
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--height', `${rect.height}px`)
    },
    async expandAllRows () {
      await this.$nextTick()
      const hasSearch = Boolean(this.search.groupId || this.search.questionType || this.search.keywords)
      const tableRef = this.tableRef
      _.forEach(this.searchResult, group => {
        tableRef.toggleRowExpansion(group, hasSearch)
      })
    }
  },
  watch: {
    data: {
      handler () {
        this.refreshGroupList()
      },
      immediate: true
    },
    selectedWatcher: {
      handler () {
        this.refreshSelected()
      },
      immediate: true
    },
    search: {
      handler () {
        this.expandAllRows()
      },
      deep: true
    },
    questionTypeList: {
      handler () {
        if (this.questionTypeList === 1) {
          this.search.questionType = this.questionTypeList[0].value
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.group-question-selector {
  ::v-deep {
    .table-list-wrapper {
      .table-header {
        .filter-group {
          margin-right: 0;
        }
      }

      .table-container {
        td {
          padding: 10px 0;
        }

        td.group-name {
          .cell {
            text-align: left;
          }
        }

        td.question-title {
          .cell {
            padding: 0 10px;
            text-align: left;
          }
        }
      }
    }
  }

  .question-title-wrapper {
    display: flex;
    line-height: normal;

    .title {
      flex: 1;
      text-overflow: ellipsis;
      white-space: pre;
      overflow: hidden;
    }

    .expand-btn {
      margin: 0 0 auto auto;
      padding: 0;
      line-height: inherit;
    }
  }

  .question-options {
    padding-left: 18px;
    color: #999;
    list-style-type: disc;
    overflow: hidden;
    transition: height .3s;

    &.flip-enter-active {
      height: 0;
    }

    &.flip-leave-active {
      height: var(--height, auto);
    }

    &.flip-enter-to {
      height: var(--height, auto);
    }

    &.flip-leave-to {
      height: 0;
    }
  }
}

.el-select-dropdown__item {
  min-width: 100%;
  max-width: 40vw;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: ellipsis;
}
</style>
